import React, { useEffect, useState } from "react";
import { History, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatINR = (n) => `₹${new Intl.NumberFormat("en-IN").format(n || 0)}`;

export default function BudgetHistoryCard() {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/budget/history`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setMonths(data.months || []);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || months.length <= 1) return null;
  // Skip the most recent (current month) — it's already shown in the main budget card
  const pastMonths = months.slice(1);
  if (pastMonths.length === 0) return null;

  const visible = expanded ? pastMonths : pastMonths.slice(0, 3);

  return (
    <div className="cartiq-card" data-testid="budget-history-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
            — Budget history
          </div>
          <h2 className="font-display uppercase text-2xl flex items-center gap-2">
            <History className="w-5 h-5 text-[#FF6B00]" /> Past months
          </h2>
        </div>
      </div>

      <div className="space-y-2" data-testid="budget-history-list">
        {visible.map((m) => {
          const pct = Math.min(m.pctUsed || 0, 200);
          const over = m.isOver;
          const barColor = over ? "#E24B4A" : pct >= 80 ? "#FF6B00" : "#1D9E75";
          return (
            <div
              key={m.month}
              className="border border-[#2A2A35] bg-[#0F0F14] px-3 py-2.5"
              data-testid={`budget-history-row-${m.month}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-sm text-[#F0EDE8] font-bold uppercase tracking-wider">
                  {m.label}
                </div>
                <div className="font-mono-num text-xs">
                  <span className={over ? "text-[#E24B4A]" : "text-[#F0EDE8]"}>
                    {formatINR(m.spent)}
                  </span>
                  <span className="text-[#555]"> / {formatINR(m.budget)}</span>
                </div>
              </div>
              <div className="w-full h-1.5 bg-[#2A2A35] overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${Math.min(pct, 100)}%`,
                    background: barColor,
                    transition: "width 500ms ease-out",
                  }}
                />
              </div>
              <div className="flex justify-between mt-1 text-[10px] uppercase tracking-[0.18em] text-[#888] font-mono-num">
                <span>{pct}% used</span>
                <span>{over ? "Over budget" : `${formatINR(m.remaining)} saved`}</span>
              </div>
            </div>
          );
        })}
      </div>

      {pastMonths.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#888] hover:text-[#F0EDE8] py-2 border border-[#2A2A35] hover:border-[#FF6B00]/40 transition-colors"
          data-testid="budget-history-toggle"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
          {expanded ? "Show less" : `Show all ${pastMonths.length} months`}
        </button>
      )}
    </div>
  );
}
